import { useClientTranslation } from '@/lib/core';
import { ScreenHead } from '@/components/shared/screen/head';
import { Icon } from '@/components/ui/icon';
import { PreferenceEntry, SubcategorySection } from './index';

// ─── ProfilePreferencesSummary ────────────────────────────────────────────────

export interface ProfilePreferencesSummaryProps {
  /** Active category name — shown as muted suffix in the header: "YOUR INTERESTS EAT & DRINK" */
  categoryName?: string;
  yourInterestsLabel?: string;
  /** Instructional hint rendered below the header, e.g. "Tap a bookmark to remove…" */
  hintText?: string;
  /** Subcategory sections; only items with isSelected=true are rendered. */
  subcategories: SubcategorySection[];
  onBack: () => void;
  onToggle: (id: string, isSelected: boolean) => Promise<void>;
}

export function ProfilePreferencesSummary({
  categoryName,
  yourInterestsLabel,
  hintText,
  subcategories,
  onBack,
  onToggle,
}: ProfilePreferencesSummaryProps) {
  const t = useClientTranslation();
  const _yourInterestsLabel = yourInterestsLabel ?? t('App.Labels.YourInterests');

  return (
    <div className="flex h-full flex-col bg-white">
      <ScreenHead title={_yourInterestsLabel} titleSuffix={categoryName} onBack={onBack} className="pt-0!" />

      {/* Scrollable content */}
      <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-4 pb-8">
        {hintText && (
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px]">
            {hintText}
          </p>
        )}

        <div className="flex flex-col gap-8">
          {subcategories.map(sub => {
            const selected = sub.items.filter((item: PreferenceEntry) => item.isSelected);
            if (!selected.length) return null;

            return (
              <div key={sub.id} className="flex flex-col">
                <div className="flex h-[57px] items-center">
                  <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] leading-[29px] tracking-[0.5px] uppercase">
                    {t('App.Labels.PreferencesSectionTitle', {
                      subcategoryName: sub.name,
                      count: selected.length,
                    })}
                  </span>
                </div>
                <div className="flex flex-col">
                  {selected.map((item: PreferenceEntry) => (
                    <button
                      key={item.id}
                      type="button"
                      className="border-jncquoi-neutral-200 flex h-[57px] w-full items-center gap-4 border-b py-6 text-left"
                      onClick={() => onToggle(item.id, true)}
                    >
                      <Icon
                        name="bookmark-solid"
                        width={16}
                        height={16}
                        className="text-jncquoi-neutral-1000 shrink-0"
                      />
                      <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px] uppercase">
                        {item.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
